/**
 * @swoft/gtd-domain - MongoDB Collection Configuration
 * 
 * Centralized definition of all collection names used by the GTD bounded contexts.
 * Leverages the @swoft/mongo package for database connection management.
 * 
 * Following David Allen's GTD methodology with clean separation of:
 * - Inbox Management (capture)
 * - Project Management (organize/clarify)  
 * - Task Management (engage)
 * - Reference Management (reference materials)
 */

import { getMongoDb } from "@swoft/persistence";
import type { Collection, Document } from 'mongodb';


/**
 * MongoDB Collection Names for GTD Domain Package
 * 
 * Standardized naming convention: gtd_{context}_{entity}
 * Resolves inconsistencies between repositories and controllers
 */
export const GTD_COLLECTIONS = {
  // Inbox Management Bounded Context
  INBOX_ITEMS: 'gtd_inbox_items',
  
  // Project Management Bounded Context  
  PROJECTS: 'gtd_projects',
  NEXT_ACTIONS: 'gtd_next_actions',
  
  // Task Management Bounded Context
  TASK_ASSIGNMENTS: 'gtd_task_assignments',
  
  // Design Implementation Coordination Bounded Context
  DESIGN_IMPLEMENTATION_FLOWS: 'gtd_design_implementation_flows',
  
  // Reference Management Bounded Context
  REFERENCE_ITEMS: 'gtd_reference_items',
  AGENT_API_KEYS: 'gtd_agent_api_keys',
  
  // Someday/Maybe List
  SOMEDAY_MAYBE: 'gtd_someday_maybe'
} as const;

/**
 * Type-safe collection name type
 */
export type GTDCollectionName = typeof GTD_COLLECTIONS[keyof typeof GTD_COLLECTIONS];

/**
 * Get a MongoDB collection using the centralized @swoft/mongo package
 * 
 * @param collectionName - The collection constant from GTD_COLLECTIONS
 * @returns MongoDB Collection instance
 */
export function getGTDCollection<T extends Document = Document>(collectionName: GTDCollectionName): Collection<T> {
  const db = getMongoDb(); // Uses existing @swoft/mongo package
  return db.collection<T>(collectionName);
}

/**
 * Collection metadata with bounded context mapping
 * Following David Allen's GTD methodology and DDD principles
 */
export const GTD_COLLECTION_METADATA = {
  [GTD_COLLECTIONS.INBOX_ITEMS]: {
    boundedContext: 'inbox-management',
    description: 'Captured thoughts, ideas, and information awaiting processing (GTD Capture phase)',
    primaryKey: 'itemId',
    gtdPhase: 'capture',
    aggregateRoot: 'InboxItem'
  },
  [GTD_COLLECTIONS.PROJECTS]: {
    boundedContext: 'project-management', 
    description: 'Multi-step outcomes and projects with desired results (GTD Organize phase)',
    primaryKey: 'projectId',
    gtdPhase: 'organize',
    aggregateRoot: 'Project'
  },
  [GTD_COLLECTIONS.NEXT_ACTIONS]: {
    boundedContext: 'project-management',
    description: 'Physical next actions with context and energy requirements (GTD Organize/Engage phase)',
    primaryKey: 'actionId',
    gtdPhase: 'engage',
    aggregateRoot: 'NextAction'
  },
  [GTD_COLLECTIONS.TASK_ASSIGNMENTS]: {
    boundedContext: 'task-management',
    description: 'Task assignments and delegation tracking with status and metadata',
    primaryKey: 'assignmentId',
    gtdPhase: 'engage',
    aggregateRoot: 'TaskAssignment'
  },
  [GTD_COLLECTIONS.DESIGN_IMPLEMENTATION_FLOWS]: {
    boundedContext: 'design-implementation-coordination',
    description: 'Design-to-implementation coordination flows with artifact tracking and conformance validation',
    primaryKey: 'flowId',
    gtdPhase: 'organize',
    aggregateRoot: 'DesignImplementationFlow'
  },
  [GTD_COLLECTIONS.REFERENCE_ITEMS]: {
    boundedContext: 'reference-management',
    description: 'Reference materials and information for future use (GTD Reference system)',
    primaryKey: 'referenceId',
    gtdPhase: 'organize',
    aggregateRoot: 'ReferenceItem'
  },
  [GTD_COLLECTIONS.AGENT_API_KEYS]: {
    boundedContext: 'reference-management',
    description: 'API keys and agent configurations for external integrations',
    primaryKey: 'keyId',
    gtdPhase: 'organize',
    aggregateRoot: 'AgentApiKey'
  }
} as const;

/**
 * Package Information (following @swoft/dev-ops pattern)
 */
export const GTD_DOMAIN_PACKAGE_INFO = {
  name: '@swoft/gtd-domain',
  version: '0.1.0',
  description: 'Getting Things Done (GTD) methodology implementation with Domain-Driven Design',
  architecture: 'Clean Architecture + Domain-Driven Design',
  methodology: 'David Allen GTD (Getting Things Done)',
  boundedContexts: [
    'Inbox Management',
    'Project Management', 
    'Task Management',
    'Reference Management'
  ],
  gtdPhases: [
    'Capture',
    'Clarify', 
    'Organize',
    'Reflect',
    'Engage'
  ],
  collections: Object.values(GTD_COLLECTIONS),
  databaseDependency: '@swoft/mongo'
} as const;

/**
 * Collection Migration Guide
 * 
 * BREAKING CHANGES from previous hardcoded collection names:
 * 
 * OLD → NEW (STANDARDIZED)
 * 'gtd_inbox' → 'gtd_inbox_items' (resolves controller/repository mismatch)
 * 'swoft_builder__task_assignments' → 'gtd_task_assignments' (consistent naming)
 * 'gtd_reference_store__reference_items' → 'gtd_reference_items' (simplified)
 * 'gtd_reference_store__agent_api_keys' → 'gtd_agent_api_keys' (simplified)
 * 
 * CONSISTENT (no changes needed):
 * 'gtd_projects' ✓
 * 'gtd_next_actions' ✓
 */
export const GTD_COLLECTION_MIGRATION_GUIDE = {
  migrations: [
    {
      from: 'gtd_inbox',
      to: GTD_COLLECTIONS.INBOX_ITEMS,
      reason: 'Resolve mismatch between InboxItemAggregateRepository and GTDSystemController',
      affectedFiles: [
        'InboxItemAggregateRepository.ts',
        'GTDSharedQueryRepository.ts'
      ]
    },
    {
      from: 'swoft_builder__task_assignments', 
      to: GTD_COLLECTIONS.TASK_ASSIGNMENTS,
      reason: 'Consistent GTD naming convention',
      affectedFiles: [
        'TaskMongoRepository.ts'
      ]
    },
    {
      from: 'gtd_reference_store__reference_items',
      to: GTD_COLLECTIONS.REFERENCE_ITEMS, 
      reason: 'Simplified naming without nested prefixes',
      affectedFiles: [
        'collectionNames.ts'
      ]
    },
    {
      from: 'gtd_reference_store__agent_api_keys',
      to: GTD_COLLECTIONS.AGENT_API_KEYS,
      reason: 'Simplified naming without nested prefixes', 
      affectedFiles: [
        'collectionNames.ts'
      ]
    }
  ]
} as const;